/**
 * PlatformLogo — small branded tile for a connector platform.
 *
 * 18×18 by default; 4px corner radius (fallback branch only). When
 * `connectorType` matches an entry in `PLATFORM_GLYPHS`, the component
 * renders an inline SVG of the brand mark at the brand hex on a
 * transparent background. Otherwise it falls back to a colored tile
 * with the platform's first letter. The tile is decorative — adjacent
 * text supplies the platform display name to assistive tech.
 */
import React from 'react';
import { getPlatformMeta } from './platformLogos';
import { PLATFORM_GLYPHS, LOW_CONTRAST_OVERRIDE } from './platformGlyphs';

export interface PlatformLogoProps {
  connectorType: string;
  size?: number;
  className?: string;
}

export const PlatformLogo: React.FC<PlatformLogoProps> = ({
  connectorType,
  size = 18,
  className,
}) => {
  const meta = getPlatformMeta(connectorType);
  const glyph = PLATFORM_GLYPHS[connectorType as keyof typeof PLATFORM_GLYPHS];

  if (glyph) {
    const hex = LOW_CONTRAST_OVERRIDE[connectorType as keyof typeof PLATFORM_GLYPHS] ?? glyph.hex;
    return (
      <span
        data-platform-logo
        title={meta.displayName}
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          width={size}
          height={size}
          fill={`#${hex}`}
        >
          <path d={glyph.path} />
        </svg>
      </span>
    );
  }

  return (
    <span
      data-platform-logo
      title={meta.displayName}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: 4,
        backgroundColor: meta.bg,
        color: 'white',
        fontSize: Math.round(size * 0.55),
        fontWeight: 700,
        flexShrink: 0,
        userSelect: 'none',
      }}
      aria-hidden="true"
    >
      {meta.letter}
    </span>
  );
};
